import React, { useEffect, useMemo, useState } from "react";
import { X } from "@phosphor-icons/react";
import { useParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import Icon from "components/Icon/Icon";
import Button from "components/Buttons";
import Input from "components/Input/index";
import RadioLabelComponent from "components/Toggles/RadioLabelComponent";
import LoadingSpinner from "components/PageLoader/Spinner";

import { useTalentAgenciesSlice } from "pages/TalentAgencies/slice";
import {
  addManagerSuccess,
  updateMemberStatus
} from "pages/TalentAgencies/slice/selector";

import { TalentAgencyManagerStyles } from "./styled";

/* -------------------------------------------------------------------------- */
/* Utils */
/* -------------------------------------------------------------------------- */

const buildFormState = (rowData) => ({
  name: rowData?.name || "",
  phone_number: rowData?.phone_number || "",
  email: rowData?.email || "",
  accessLevel: rowData?.role === "admin" ? "admin" : "standard",
  canBeManager:
    rowData?.creator_manager === "Yes" ? "can_be_manager" : "not_a_manager"
});

/* -------------------------------------------------------------------------- */
/* Component */
/* -------------------------------------------------------------------------- */

const TalentAgencyManagerModal = ({ show, onHide, mode, rowData }) => {
  const dispatch = useDispatch();
  const { actions } = useTalentAgenciesSlice();
  const { agencyId } = useParams();

  const isEditMode = mode === "edit";

  const {
    success: updateSuccess,
    error: updateError,
    loading: isUpdating
  } = useSelector(updateMemberStatus);

  const {
    success: addSuccess,
    error: addError,
    loading: isAdding
  } = useSelector(addManagerSuccess);

  /* -------------------------------------------------------------------------- */
  /* State */
  /* -------------------------------------------------------------------------- */

  const [form, setForm] = useState(buildFormState());
  const [initialForm, setInitialForm] = useState(null);
  const [phoneError, setPhoneError] = useState("");
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);

  /* -------------------------------------------------------------------------- */
  /* Effects */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    if (!show) return;

    const data = buildFormState(isEditMode ? rowData : null);

    setForm(data);
    setInitialForm(data);
    setPhoneError("");
    setIsPhoneFocused(false);

    // Reset Redux add manager status to avoid modal auto-close
    dispatch(actions.resetAddManagerStatus());
  }, [show, isEditMode, rowData]);

  /* ------------------ Handle add/update success ------------------ */
  useEffect(() => {
    if (addSuccess || updateSuccess) {
      handleClose();
    }
  }, [addSuccess, updateSuccess]);

  /* ------------------ Handle add/update error ------------------ */
  useEffect(() => {
    if (addError) setPhoneError(addError);
    if (updateError) setPhoneError(updateError);
  }, [addError, updateError]);

  /* -------------------------------------------------------------------------- */
  /* Handlers */
  /* -------------------------------------------------------------------------- */

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handlePhoneChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
    updateField("phone_number", digitsOnly);

    if (phoneError) setPhoneError("");
  };

  const handlePhoneKeyDown = (e) => {
    if (["ArrowUp", "ArrowDown"].includes(e.key)) e.preventDefault();
  };

  const handleSubmit = () => {
    const payload = {
      name: form.name,
      email: form.email.trim() || "",
      phone_number: form.phone_number,
      access_level: form.accessLevel === "admin" ? "admin" : "user",
      talent_company_id: agencyId,
      can_be_creator_manager: form.canBeManager === "can_be_manager"
    };

    if (isEditMode) {
      dispatch(
        actions.updateTeamMember({
          ...payload,
          userId: rowData.id
        })
      );
    } else {
      dispatch(
        actions.addTalentAgencyManager({
          ...payload,
          can_be_creator_manager: form.canBeManager === "can_be_manager"
        })
      );
    }
  };

  /* ---------------------- Modal close ---------------------- */
  const handleClose = () => {
    onHide();
    dispatch(actions.resetAddManagerStatus());
  };

  /* -------------------------------------------------------------------------- */
  /* Derived State */
  /* -------------------------------------------------------------------------- */

  const isDirty = useMemo(() => {
    if (!isEditMode || !initialForm) return true;

    return Object.keys(initialForm).some(
      (key) => form[key] !== initialForm[key]
    );
  }, [form, initialForm, isEditMode]);

  const isSubmitDisabled = useMemo(() => {
    if (!form.name.trim()) return true;
    if (form.phone_number.length !== 10) return true;
    if (isEditMode && !isDirty) return true;

    return false;
  }, [form, isEditMode, isDirty]);

  /* -------------------------------------------------------------------------- */
  /* Render */
  /* -------------------------------------------------------------------------- */

  return (
    <TalentAgencyManagerStyles
      show={show}
      onHide={handleClose}
      backdrop
      keyboard
      unmountOnExit
      restoreFocus={false}
      enforceFocus={false}
      scrollable={false}
      className="sidebar-modal"
    >
      <Modal.Header>
        <Modal.Title>
          {isEditMode ? "Update Team Member" : "Add Team Member"}
        </Modal.Title>

        <div className="close-icon-wrapper" onClick={onHide}>
          <Icon component={X} size={24} />
        </div>
      </Modal.Header>

      <Modal.Body>
        <Input
          id="name"
          label="Name"
          required
          value={form.name}
          onChange={(e) => updateField("name", e.target.value)}
        />

        <div className="phone-field-wrapper">
          <Input
            id="phone_number"
            label="Phone"
            type="number"
            value={form.phone_number}
            onChange={handlePhoneChange}
            onKeyDown={handlePhoneKeyDown}
            hasError={phoneError}
            helperText={phoneError}
            inputMode="numeric"
            inputProps={{
              onFocus: () => setIsPhoneFocused(true),
              onBlur: () => setIsPhoneFocused(false)
            }}
          />

          {isPhoneFocused && (
            <div className="notes-wrapper">
              <h5>NOTE:</h5>
              <p>
                Updating the login number will disable the previous one. Only
                the new number can be used to sign in.
              </p>
            </div>
          )}
        </div>

        <Input
          id="email"
          label="Email"
          value={form.email}
          onChange={(e) => {
            updateField("email", e.target.value);
          }}
        />

        <div className="access-level-wrapper">
          <h5>Access Level</h5>

          <RadioLabelComponent
            checked={form.accessLevel === "standard"}
            labelHeader="Standard"
            labelDescription="Gets access to Signal"
            onChange={() => updateField("accessLevel", "standard")}
          />

          {/* <RadioLabelComponent
            checked={form.accessLevel === "admin"}
            labelHeader="Admin"
            labelDescription="Signal Access + Add/Remove Users, Settings, etc."
            onChange={() => updateField("accessLevel", "admin")}
          /> */}
        </div>

        <div className="access-level-wrapper">
          <h5>Creator Management</h5>

          <RadioLabelComponent
            checked={form.canBeManager === "can_be_manager"}
            labelHeader="Can be a Creator Manager"
            labelDescription="Creators can be assigned to them and they’ll appear on the HashFame app."
            onChange={() => updateField("canBeManager", "can_be_manager")}
            isEditMode={isEditMode}
          />

          <RadioLabelComponent
            checked={form.canBeManager === "not_a_manager"}
            labelHeader="Not a Creator Manager"
            labelDescription="Creators cannot be assigned and they won’t appear on the HashFame app."
            onChange={() => updateField("canBeManager", "not_a_manager")}
            isEditMode={isEditMode}
          />
        </div>

        <div className="notes-wrapper">
          <h5>NOTE:</h5>
          <p>
            Creator Manager status (visibility on the HashFame app) can’t be
            changed later.
          </p>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitDisabled || isAdding || isUpdating}
        >
          {isAdding || isUpdating ? (
            <LoadingSpinner size={16} color="#fff" />
          ) : isEditMode ? (
            "Save Changes"
          ) : (
            "Add Team Member"
          )}
        </Button>
      </Modal.Footer>
    </TalentAgencyManagerStyles>
  );
};

export default TalentAgencyManagerModal;
